import { watch, ref, onUnmounted, computed, nextTick } from "vue";
import { useAutoSave } from "./useAutoSave";

/**
 * Form-specific auto-save composable with ADR-compliant architecture
 * @param {Object} store - Pinia store instance
 * @param {Ref|Reactive} formData - Form data object
 * @param {Object} options - Configuration options
 */
export function useFormAutoSave(store, formData, options = {}) {
  const {
    saveMethod = "save",
    saveMethodParams = null,
    debounceMs = 1000,
    maxRetries = 2,
    retryDelay = 1000,
    enableRateLimiting = true,
    trackChanges = true,
    enableBeforeUnload = true,
    excludeFields = [],
    validator = null,
    transformer = null,
  } = options;

  const hasUnsavedChanges = ref(false);
  const isApplyingServerUpdate = ref(false);

  const autoSave = useAutoSave({
    debounceMs,
    maxRetries,
    retryDelay,
    enableRateLimiting,
    validator:
      validator ||
      ((data) => {
        if (!data || typeof data !== "object") {
          return { isValid: false, message: "Invalid form data" };
        }
        return { isValid: true, message: "" };
      }),
    onSaveSuccess: () => {
      hasUnsavedChanges.value = false;
    },
    onSaveError: (error) => {
      console.error("Form save failed:", error);
    },
  });

  const saveInProgress = computed(() => autoSave.isSaving.value);
  const hasErrors = computed(() => autoSave.saveErrors.value.length > 0);

  const resolveSaveParams = (data, context) => {
    if (typeof saveMethodParams === "function") {
      return saveMethodParams(data, context, formData);
    }
    if (Array.isArray(saveMethodParams)) {
      return saveMethodParams;
    }
    if (saveMethodParams && typeof saveMethodParams === "object") {
      return saveMethodParams;
    }
    return [];
  };

  const getFilteredFormData = (data) => {
    if (excludeFields.length === 0) return data;
    const filtered = { ...data };
    for (const field of excludeFields) {
      delete filtered[field];
    }
    return filtered;
  };

  const transformData = (data) => (transformer ? transformer(data) : data);

  const executeSave = async (data, context = {}) => {
    const filteredData = getFilteredFormData(data);
    const transformedData = transformData(filteredData);

    isApplyingServerUpdate.value = true;
    try {
      if (typeof store[saveMethod] !== "function") {
        throw new TypeError(`Store method '${saveMethod}' is not a function`);
      }

      const params = resolveSaveParams(transformedData, context);

      if (Array.isArray(params) && params.length > 0) {
        return await store[saveMethod](transformedData, ...params);
      } else if (params && typeof params === "object" && !Array.isArray(params)) {
        return await store[saveMethod](transformedData, params);
      } else {
        return await store[saveMethod](transformedData);
      }
    } finally {
      nextTick(() => {
        isApplyingServerUpdate.value = false;
      });
    }
  };

  const debouncedSave = autoSave.createDebouncedSave(executeSave, debounceMs);

  const saveManually = async (additionalContext = {}) => {
    const currentData = formData.value || formData;
    return await autoSave.forceSave(executeSave, currentData, {
      manual: true,
      ...additionalContext,
    });
  };

  const resetToLastSaved = () => {
    if (autoSave.originalData?.value) {
      Object.assign(formData, autoSave.originalData.value);
      autoSave.clearDirtyFields();
    }
  };

  if (trackChanges) {
    watch(
      formData,
      (newData) => {
        if (!newData || isApplyingServerUpdate.value) return;

        autoSave.trackDirtyFields(newData);
        hasUnsavedChanges.value = autoSave.isDirty();

        if (autoSave.isDirty()) {
          debouncedSave(newData, {
            operationType: "save",
            timestamp: Date.now(),
          });
        }
      },
      { deep: true, immediate: false },
    );
  }

  const handleBeforeUnload = (event) => {
    if (saveInProgress.value || hasErrors.value) {
      event.preventDefault();
      event.returnValue = "";
      return "";
    }
  };

  const handleVisibilityChange = () => {
    if (document.hidden && hasUnsavedChanges.value) {
      saveManually().catch(console.error);
    }
  };

  if (enableBeforeUnload) {
    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);
  }

  onUnmounted(() => {
    if (enableBeforeUnload) {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    }

    if (hasUnsavedChanges.value && !saveInProgress.value) {
      saveManually().catch(console.error);
    }
  });

  const getFormStatus = () => ({
    hasUnsavedChanges: hasUnsavedChanges.value,
    saveInProgress: saveInProgress.value,
    lastSaved: autoSave.lastSaved.value,
    dirtyFields: autoSave.getDirtyFields(),
    errorCount: autoSave.saveErrors.value.length,
  });

  return {
    ...autoSave,
    hasUnsavedChanges,
    saveInProgress,
    hasErrors,
    debouncedSave,
    saveManually,
    resetToLastSaved,
    getFormStatus,
    isFormDirty: hasUnsavedChanges,
    canSave: computed(
      () => !saveInProgress.value && hasUnsavedChanges.value && !autoSave.isRateLimited(),
    ),
  };
}
